/**
 * Aquafire model details (sections/aquafire-model-details.liquid)
 *
 * Below the configurator: the picked fireplace's feature icons and accordions, copied from its product page so they're
 * only edited there. The configurator (assets/aquafire-configurator.js) announces the model with an
 * "aquafire:model-change" event and exposes it as `selectedModel`; this element gets that model's product page from the
 * configurator's loader (window.AquafireProductPages, shared with the model cards' icons) when the section comes near
 * the screen, or at once from the copy remembered on an earlier visit.
 */
(() => {
  if (window.customElements.get('aquafire-model-details')) {
    return;
  }

  class AquafireModelDetails extends HTMLElement {
    connectedCallback() {
      this.model = null;
      this.inner = this.querySelector('[data-afc-details-inner]');
      this.heading = this.querySelector('[data-afc-details-heading]');
      this.link = this.querySelector('[data-afc-details-link]');
      this.features = this.querySelector('[data-afc-details-features]');
      this.accordions = this.querySelector('[data-afc-details-accordions]');
      this.videos = [...this.querySelectorAll('[data-afc-details-video]')];

      // Its content is hidden until loaded; this element stays in the page (with no height) to be watched
      this.nearScreen = new Promise((resolve) => {
        if (!('IntersectionObserver' in window)) {
          resolve();
          return;
        }

        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            resolve();
          }
        }, { rootMargin: '0px 0px 1200px 0px' });
        this.observer.observe(this);
      });

      this.onModelChange = (event) => this.show(event.detail);
      document.addEventListener('aquafire:model-change', this.onModelChange);

      const model = document.querySelector('aquafire-configurator')?.selectedModel;

      if (model) {
        this.show(model);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('aquafire:model-change', this.onModelChange);
      this.observer?.disconnect();
    }

    async show(model) {
      if (!model?.url || model.url === this.model?.url) {
        return;
      }

      this.model = model;
      this.showVideo(model);
      this.setAttribute('aria-busy', 'true');

      // Remembered from an earlier visit: shown at once, then refreshed
      const cached = window.AquafireProductPages?.cached(model.url);

      if (cached) {
        this.render(model, cached);
      }

      try {
        await this.nearScreen;
        const parts = await this.load(model.url);

        if (this.model !== model) {
          return;
        }

        this.render(model, parts);
      } catch (error) {
        // The product page couldn't be loaded: nothing to show, rather than another model's details (but the model's
        // own video can still be shown)
        if (this.model === model && this.shown !== model.url) {
          this.fill(this.features, '');
          this.fill(this.accordions, '');
          this.heading.textContent = (this.dataset.heading || '[model]').replace('[model]', model.name);
          this.inner.hidden = !this.hasVideo(model);
        }
      } finally {
        if (this.model === model) {
          this.removeAttribute('aria-busy');
        }
      }
    }

    render(model, parts) {
      this.shown = model.url;
      this.heading.textContent = (this.dataset.heading || '[model]').replace('[model]', model.name);

      if (this.link) {
        this.link.href = model.url;
      }

      this.fill(this.features, parts.features);
      this.fill(this.accordions, parts.accordions);
      this.inner.hidden = !(this.features?.innerHTML || this.accordions?.innerHTML || this.hasVideo(model));
    }

    // The picked model's quick start video; the others are hidden and put back to their picture, which stops them
    showVideo(model) {
      this.videos.forEach((video) => {
        const isModels = video.dataset.afcDetailsVideo === model.key;

        if (!isModels) {
          window.AquafireVideo?.reset(video);
        }

        video.hidden = !isModels;
      });
    }

    hasVideo(model) {
      return this.videos.some((video) => video.dataset.afcDetailsVideo === model.key);
    }

    fill(container, html) {
      if (!container) {
        return;
      }

      // The theme's own markup, so its styles and accordion behavior apply as on the product page. Unchanged content is
      // left alone so an open accordion stays open.
      if (container.dataset.html !== html) {
        container.dataset.html = html;
        container.innerHTML = html;
      }

      container.hidden = !html;
    }

    load(url) {
      // Shared with the configurator, which loads the picked model's page for its card (assets/aquafire-configurator.js)
      const pages = window.AquafireProductPages;
      return pages ? pages.load(url) : Promise.reject(new Error('The configurator script is missing'));
    }
  }

  window.customElements.define('aquafire-model-details', AquafireModelDetails);
})();
